import React, { useState, useEffect, useCallback } from 'react';
import { 
  getAdminSections, 
  createSection, 
  updateSection, 
  deleteSection, 
  reorderSections,
  getCompletionStats 
} from '../services/handbookService';
import ReactMarkdown from 'react-markdown';
import './HandbookEditor.css';

const HandbookEditor = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [completionStats, setCompletionStats] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    chapter: '',
    title: '',
    content: '',
    summary: '',
    sort_order: 0,
    requires_acknowledgment: false,
    is_active: true
  });

  const fetchSections = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAdminSections();
      setSections(data.sections || []);
    } catch (err) {
      setError('Failed to load handbook sections');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = async () => {
    try {
      const data = await getCompletionStats();
      setCompletionStats(data.stats || []);
      setShowStats(true);
    } catch (err) {
      console.error('Failed to load completion stats:', err);
    }
  };

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const handleSelectSection = (section) => {
    setSelectedSection(section);
    setFormData({
      chapter: section.chapter || '',
      title: section.title || '',
      content: section.content || '',
      summary: section.summary || '',
      sort_order: section.sort_order || 0,
      requires_acknowledgment: section.requires_acknowledgment || false,
      is_active: section.is_active !== false
    });
    setIsEditing(false);
    setIsCreating(false);
  };

  const handleCreate = () => {
    setSelectedSection(null);
    setFormData({
      chapter: String(sections.length + 1),
      title: '',
      content: '',
      summary: '',
      sort_order: (sections.length + 1) * 10,
      requires_acknowledgment: true,
      is_active: true
    });
    setIsCreating(true);
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (isCreating) {
        await createSection(formData);
      } else if (selectedSection) {
        await updateSection(selectedSection.id, formData);
      }
      
      await fetchSections();
      setIsEditing(false);
      setIsCreating(false);
      
      // Re-select the section to show updated data
      if (!isCreating && selectedSection) {
        const updated = sections.find(s => s.id === selectedSection.id);
        if (updated) setSelectedSection(updated);
      }
    } catch (err) {
      setError('Failed to save section');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSection) return;
    
    if (!window.confirm(`Are you sure you want to delete "${selectedSection.title}"?`)) {
      return;
    }
    
    try {
      await deleteSection(selectedSection.id);
      await fetchSections();
      setSelectedSection(null);
      setIsEditing(false);
    } catch (err) {
      setError('Failed to delete section');
      console.error(err);
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    
    const newSections = [...sections];
    [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    
    const order = newSections.map((s, i) => ({
      id: s.id,
      sort_order: (i + 1) * 10
    }));
    
    try {
      await reorderSections(order);
      await fetchSections();
    } catch (err) {
      console.error('Failed to reorder sections:', err);
    }
  };

  const handleMoveDown = async (index) => {
    if (index === sections.length - 1) return;
    
    const newSections = [...sections];
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    
    const order = newSections.map((s, i) => ({
      id: s.id,
      sort_order: (i + 1) * 10
    }));
    
    try {
      await reorderSections(order);
      await fetchSections();
    } catch (err) {
      console.error('Failed to reorder sections:', err);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="handbook-editor">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading handbook sections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="handbook-editor">
      {/* Header */}
      <div className="editor-header">
        <div className="header-left">
          <h1>📖 Handbook Editor</h1>
          <p className="subtitle">Manage employee handbook sections</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={fetchStats}>
            📊 View Stats
          </button>
          <button className="btn btn-primary" onClick={handleCreate}>
            + Add Section
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="editor-layout">
        {/* Sections List */}
        <div className="sections-panel">
          <div className="panel-header">
            <h3>Sections ({sections.length})</h3>
          </div>
          <div className="sections-list">
            {sections.map((section, index) => (
              <div 
                key={section.id}
                className={`section-item ${selectedSection?.id === section.id ? 'selected' : ''} ${!section.is_active ? 'inactive' : ''}`}
                onClick={() => handleSelectSection(section)}
              >
                <div className="section-item-content">
                  <span className="chapter-badge">{section.chapter}</span>
                  <div className="section-info">
                    <h4>{section.title}</h4>
                    {section.summary && (
                      <p className="section-summary">{section.summary}</p>
                    )}
                  </div>
                  {section.requires_acknowledgment && (
                    <span className="ack-badge" title="Requires Acknowledgment">✓</span>
                  )}
                </div>
                <div className="section-actions">
                  <button 
                    className="btn-icon" 
                    onClick={(e) => { e.stopPropagation(); handleMoveUp(index); }}
                    disabled={index === 0}
                    title="Move Up"
                  >
                    ↑
                  </button>
                  <button 
                    className="btn-icon"
                    onClick={(e) => { e.stopPropagation(); handleMoveDown(index); }}
                    disabled={index === sections.length - 1}
                    title="Move Down"
                  >
                    ↓
                  </button>
                </div>
              </div>
            ))}
            
            {sections.length === 0 && (
              <div className="empty-state">
                <p>No handbook sections yet.</p>
                <button className="btn btn-primary" onClick={handleCreate}>
                  Create First Section
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Editor Panel */}
        <div className="editor-panel">
          {(selectedSection || isCreating) ? (
            <>
              <div className="panel-header">
                <h3>{isCreating ? 'New Section' : 'Edit Section'}</h3>
                <div className="panel-actions">
                  {!isEditing ? (
                    <>
                      <button className="btn btn-secondary" onClick={() => setShowPreview(!showPreview)}>
                        {showPreview ? '📝 Edit View' : '👁 Preview'}
                      </button>
                      <button className="btn btn-primary" onClick={() => setIsEditing(true)}>
                        Edit
                      </button>
                      <button className="btn btn-danger" onClick={handleDelete}>
                        Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-secondary" onClick={() => {
                        setIsEditing(false);
                        setIsCreating(false);
                        if (selectedSection) handleSelectSection(selectedSection);
                      }}>
                        Cancel
                      </button>
                      <button className="btn btn-success" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="edit-form">
                  <div className="form-row">
                    <div className="form-group" style={{ flex: '0 0 100px' }}>
                      <label>Chapter</label>
                      <input
                        type="text"
                        value={formData.chapter}
                        onChange={(e) => setFormData({...formData, chapter: e.target.value})}
                        placeholder="1, 2, A..."
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Title</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        placeholder="Section title"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Summary (shown in table of contents)</label>
                    <input
                      type="text"
                      value={formData.summary}
                      onChange={(e) => setFormData({...formData, summary: e.target.value})}
                      placeholder="Brief description of this section"
                    />
                  </div>

                  <div className="form-group">
                    <label>Content (Markdown supported)</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      placeholder="## Section Content&#10;&#10;Write your handbook content here using Markdown..."
                      rows={15}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group" style={{ flex: '0 0 150px' }}>
                      <label>Sort Order</label>
                      <input
                        type="number"
                        value={formData.sort_order}
                        onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.requires_acknowledgment}
                          onChange={(e) => setFormData({...formData, requires_acknowledgment: e.target.checked})}
                        />
                        Requires Acknowledgment
                      </label>
                    </div>
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        />
                        Active (visible to employees)
                      </label>
                    </div>
                  </div>
                </div>
              ) : showPreview ? (
                <div className="preview-panel">
                  <div className="preview-header">
                    <span className="preview-chapter">Chapter {selectedSection?.chapter}</span>
                    <h2>{selectedSection?.title}</h2>
                  </div>
                  <div className="preview-content markdown-content">
                    <ReactMarkdown>{selectedSection?.content || ''}</ReactMarkdown>
                  </div>
                </div>
              ) : (
                <div className="section-details">
                  <div className="detail-row">
                    <span className="label">Chapter:</span>
                    <span className="value">{selectedSection?.chapter}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Title:</span>
                    <span className="value">{selectedSection?.title}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Summary:</span>
                    <span className="value">{selectedSection?.summary || 'No summary'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Status:</span>
                    <span className={`status-badge ${selectedSection?.is_active ? 'active' : 'inactive'}`}>
                      {selectedSection?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Acknowledgment:</span>
                    <span className="value">
                      {selectedSection?.requires_acknowledgment ? 'Required ✓' : 'Not Required'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Last Updated:</span>
                    <span className="value">{formatDate(selectedSection?.updated_at)}</span>
                  </div>
                  
                  <div className="content-preview">
                    <h4>Content Preview:</h4>
                    <div className="markdown-content">
                      <ReactMarkdown>{selectedSection?.content || ''}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="empty-editor">
              <div className="empty-icon">📖</div>
              <h3>Select a Section</h3>
              <p>Choose a section from the list to view or edit, or create a new one.</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats Modal */}
      {showStats && (
        <div className="modal-overlay" onClick={() => setShowStats(false)}>
          <div className="modal-content stats-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📊 Employee Reading Progress</h2>
              <button className="close-btn" onClick={() => setShowStats(false)}>×</button>
            </div>
            <div className="modal-body">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th>Sections Read</th>
                    <th>Acknowledged</th>
                    <th>Progress</th>
                    <th>Last Read</th>
                  </tr>
                </thead>
                <tbody>
                  {completionStats.map((stat) => (
                    <tr key={stat.id}>
                      <td>{stat.name}</td>
                      <td>{stat.role}</td>
                      <td>{stat.sections_read} / {stat.total_sections}</td>
                      <td>{stat.sections_acknowledged}</td>
                      <td>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill"
                            style={{ width: `${(stat.sections_read / stat.total_sections) * 100}%` }}
                          />
                        </div>
                        <span className="progress-text">
                          {Math.round((stat.sections_read / stat.total_sections) * 100)}%
                        </span>
                      </td>
                      <td>{formatDate(stat.last_read_at)}</td>
                    </tr>
                  ))}
                  {completionStats.length === 0 && (
                    <tr>
                      <td colSpan="6" className="empty-row">No reading data yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandbookEditor;
